/**
 * SOFSEK — Duties (functional access bundles). Clicking a duty shows the
 * permissions BOUND to it in the right pane, READ-ONLY — so a user who can only
 * view the screen can still see exactly what a duty grants. Editing that binding
 * set moves into the duty's edit modal (alongside its other fields): the
 * cross-application permission picker, staged and applied on Save.
 *
 * History note (§38): once real scans reference bundle history (Phase 3+),
 * removing a permission from a live bundle should close the existing row's
 * effective_end_date rather than delete it — Phase 1 has no scans yet, so a plain
 * delete is safe here.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { TranslationsEditor } from '../../components/common/TranslationsEditor';
import type { TranslationRow } from '../../components/common/TranslationsEditor';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Plus } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedField } from '../../components/personalization';

const PAGE_CODE = 'SFK.BUNDLE.LIST';
const ACTIVE_LOCALES = ['EN_US', 'ES_MX', 'EN_GB'];
const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';

interface BundleRow {
  bundle_id: number;
  internal_code: string;
  materiality_amount: number | null;
  is_active: 'Y' | 'N';
  tl: { locale_code: string; display_text: string }[];
}
interface PermissionRow {
  permission_id: number;
  application_id: number;
  application_name: string;
  platform_signature: string;
  display_name: string;
}
interface BundlePermissionRow { bundle_permission_id: number; bundle_id: number; permission_id: number; }

function tlName(tl: { locale_code: string; display_text: string }[], loc: string): string {
  return tl.find(x => x.locale_code === loc)?.display_text ?? tl.find(x => x.locale_code === 'EN_US')?.display_text ?? '';
}

function BundleInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const [bundles, setBundles] = useState<BundleRow[]>([]);
  const [permissions, setPermissions] = useState<PermissionRow[]>([]);
  const [mappings, setMappings] = useState<BundlePermissionRow[]>([]);
  const [selected, setSelected] = useState<BundleRow | null>(null);
  const [bundleFilter, setBundleFilter] = useState('');
  const [permFilter, setPermFilter] = useState('');
  const [editing, setEditing] = useState<BundleRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [bundleRes, permRes, mapRes, appRes] = await Promise.all([
        apiDb.from('sfk_bundle').select('bundle_id, internal_code, materiality_amount, is_active, sfk_bundle_tl ( locale_code, display_text )').order('internal_code'),
        apiDb.from('sfk_v_action').select('permission_id, application_id, platform_signature, display_name').order('display_name'),
        apiDb.from('sfk_bundle_permission').select('bundle_permission_id, bundle_id, permission_id').is('effective_end_date', null),
        apiDb.from('csm_application_master').select('application_id, csm_application_master_tl ( locale_code, display_text )'),
      ]);
      if (bundleRes.error) throw bundleRes.error;
      if (permRes.error) throw permRes.error;
      if (mapRes.error) throw mapRes.error;
      if (appRes.error) throw appRes.error;
      const appList = ((appRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        application_id: r.application_id as number,
        name: tlName(r.csm_application_master_tl as { locale_code: string; display_text: string }[], 'EN_US') || String(r.application_id),
      }));
      const appName = (id: number) => appList.find(a => a.application_id === id)?.name ?? String(id);
      const nextBundles = ((bundleRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        bundle_id: r.bundle_id as number,
        internal_code: r.internal_code as string,
        materiality_amount: r.materiality_amount as number | null,
        is_active: r.is_active as 'Y' | 'N',
        tl: (r.sfk_bundle_tl ?? []) as BundleRow['tl'],
      }));
      setBundles(nextBundles);
      // keep the selected duty's fields fresh across reloads (e.g. after an edit)
      setSelected(prev => (prev ? nextBundles.find(b => b.bundle_id === prev.bundle_id) ?? null : null));
      setPermissions(((permRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        permission_id: r.permission_id as number,
        application_id: r.application_id as number,
        application_name: appName(r.application_id as number),
        platform_signature: r.platform_signature as string,
        display_name: r.display_name as string,
      })));
      setMappings((mapRes.data ?? []) as unknown as BundlePermissionRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  useEffect(() => { void reload(); }, []);

  const filteredBundles = useMemo(() => {
    const q = bundleFilter.trim().toLowerCase();
    return bundles.filter(b => !q || b.internal_code.toLowerCase().includes(q) || tlName(b.tl, 'EN_US').toLowerCase().includes(q));
  }, [bundles, bundleFilter]);

  // Read-only view of the permissions bound to the selected duty (searchable).
  const boundPermissions = useMemo(() => {
    if (!selected) return [];
    const boundIds = new Set(mappings.filter(m => m.bundle_id === selected.bundle_id).map(m => m.permission_id));
    const q = permFilter.trim().toLowerCase();
    return permissions
      .filter(p => boundIds.has(p.permission_id))
      .filter(p => !q || p.display_name.toLowerCase().includes(q) || p.platform_signature.toLowerCase().includes(q) || p.application_name.toLowerCase().includes(q));
  }, [selected, mappings, permissions, permFilter]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-hidden p-6">
        <PageHeader
          title={t('sfk.bundle.title', 'Functional Access Bundle Designer')}
          description={t('sfk.bundle.subtitle', 'Define duties and review the atomic permissions bound to each.')}
          actions={
            <>
              <button onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
              {canAdd && (<button onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> {t('common.new', 'New')}</button>)}
              <PersonalizationToggle />
            </>
          }
        />
        <StatStrip stats={[{ value: bundles.length, label: t('common.total', 'Total') }, { value: bundles.filter(b => b.is_active === 'Y').length, label: t('common.active', 'Active') }]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 overflow-hidden">
        {/* Left panel: duty inventory */}
        <div className="flex w-80 shrink-0 flex-col border-r border-csm-border bg-white">
          <div className="flex items-center gap-2 border-b border-csm-border p-2">
            <input value={bundleFilter} onChange={e => setBundleFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
              className="h-7 w-full rounded border border-csm-border px-2 text-csm-sm" />
            <ClearFiltersButton active={bundleFilter.trim() !== ''} onClear={() => setBundleFilter('')} />
          </div>
          <div className="flex-1 overflow-auto">
            {filteredBundles.map(b => (
              <button key={b.bundle_id} onClick={() => setSelected(b)}
                className={`flex w-full items-center justify-between border-b border-csm-border-soft px-3 py-2 text-left text-csm-sm hover:bg-csm-surface-alt ${selected?.bundle_id === b.bundle_id ? 'bg-csm-surface-alt' : ''}`}>
                <span>
                  <span className="block font-medium text-csm-ink-soft">{tlName(b.tl, 'EN_US') || b.internal_code}</span>
                  <span className="font-mono text-csm-xs text-csm-watermark">{b.internal_code}</span>
                </span>
                <span className="flex items-center gap-1">
                  <YesNoBadge value={b.is_active} />
                  {canChange && (<span onClick={(e) => { e.stopPropagation(); setEditing(b); }} className="ml-1 rounded border border-csm-border bg-white px-1.5 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.edit', 'Edit')}</span>)}
                </span>
              </button>
            ))}
            {filteredBundles.length === 0 && !busy && <div className="p-6"><EmptyState title={t('common.no_records', 'No records')} /></div>}
          </div>
        </div>

        {/* Right panel: READ-ONLY bound permissions for the selected duty */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {!selected ? (
            <div className="flex flex-1 items-center justify-center text-csm-sm text-csm-watermark">{t('sfk.bundle.select_prompt', 'Select a duty to view its bound permissions.')}</div>
          ) : (
            <>
              <div className="flex items-center gap-2 border-b border-csm-border p-2">
                <span className="text-csm-sm font-medium text-csm-ink-soft">{tlName(selected.tl, 'EN_US') || selected.internal_code}</span>
                <input value={permFilter} onChange={e => setPermFilter(e.target.value)} placeholder={t('sfk.bundle.search_bound', 'Search bound permissions…')}
                  className="ml-2 h-7 w-96 rounded border border-csm-border px-2 text-csm-sm" />
                <ClearFiltersButton active={permFilter.trim() !== ''} onClear={() => setPermFilter('')} />
                <span className="ml-auto text-csm-xs text-csm-watermark">{boundPermissions.length} {t('sfk.bundle.bound', 'bound')}</span>
              </div>
              <div className="flex-1 overflow-auto p-2">
                {boundPermissions.length === 0 && <EmptyState title={t('sfk.bundle.no_bound', 'No permissions bound to this duty yet.')} />}
                <div className="flex flex-col gap-0.5">
                  {boundPermissions.map(p => (
                    <div key={p.permission_id} className="flex items-center gap-2 rounded px-2 py-1 text-csm-sm">
                      <span className="w-40 shrink-0 truncate text-csm-xs text-csm-watermark">{p.application_name}</span>
                      <span className="flex-1">{p.display_name}</span>
                      <span className="font-mono text-csm-xs text-csm-watermark">{p.platform_signature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <BundleModal session={session} row={editing} permissions={permissions} mappings={mappings}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}
    </div>
  );
}

function BundleModal({ session, row, permissions, mappings, onClose, onSaved }: {
  session: AuthSession; row: BundleRow | null; permissions: PermissionRow[]; mappings: BundlePermissionRow[];
  onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const [code, setCode] = useState(row?.internal_code ?? '');
  const [materiality, setMateriality] = useState(row?.materiality_amount != null ? String(row.materiality_amount) : '');
  const [isActive, setIsActive] = useState(!row || row.is_active === 'Y');
  const [translations, setTranslations] = useState<TranslationRow[]>(row?.tl ?? ACTIVE_LOCALES.map(loc => ({ locale_code: loc, display_text: '' })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Permission binding editor (moved here from the right pane). Staged locally,
  // diffed against the current bindings and applied on Save.
  const originalBound = useMemo(
    () => new Set(row ? mappings.filter(m => m.bundle_id === row.bundle_id).map(m => m.permission_id) : []),
    [row, mappings],
  );
  const [selectedPerms, setSelectedPerms] = useState<Set<number>>(() => new Set(originalBound));
  const [permFilter, setPermFilter] = useState('');

  const filteredPerms = useMemo(() => {
    const q = permFilter.trim().toLowerCase();
    return permissions.filter(p => !q ||
      p.display_name.toLowerCase().includes(q) || p.platform_signature.toLowerCase().includes(q) || p.application_name.toLowerCase().includes(q));
  }, [permissions, permFilter]);

  const togglePerm = (permissionId: number) => {
    setSelectedPerms(prev => {
      const next = new Set(prev);
      if (next.has(permissionId)) next.delete(permissionId); else next.add(permissionId);
      return next;
    });
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!code.trim()) throw new Error(t('common.err.code_required', 'Code is required.'));
      if (!translations.some(tr => tr.display_text?.trim())) throw new Error(t('common.err.translation_required', 'At least one translation is required.'));

      const payload = {
        materiality_amount: materiality.trim() ? Number(materiality) : null,
        is_active: isActive ? 'Y' : 'N',
      };
      let bundleId = row?.bundle_id;
      if (row === null) {
        const { row_id, error: e } = await routedInsert(session, 'sfk_bundle', { internal_code: code.trim().toUpperCase(), ...payload });
        if (e) throw new Error(e.message);
        bundleId = row_id ?? undefined;
      } else {
        const { error: e } = await routedUpdate(session, 'sfk_bundle', 'bundle_id', row.bundle_id, payload);
        if (e) throw new Error(e.message);
      }
      if (bundleId != null) {
        await Promise.all(
          translations.filter(tr => tr.display_text?.trim()).map(tr =>
            routedInsert(session, 'sfk_bundle_tl', { bundle_id: bundleId, locale_code: tr.locale_code, display_text: tr.display_text }))
        );
        // Apply the staged permission-binding changes.
        const toAdd = [...selectedPerms].filter(id => !originalBound.has(id));
        const toRemove = [...originalBound].filter(id => !selectedPerms.has(id));
        await Promise.all(toAdd.map(pid =>
          routedInsert(session, 'sfk_bundle_permission', { bundle_id: bundleId, permission_id: pid })));
        const removeMappingIds = mappings
          .filter(m => m.bundle_id === bundleId && toRemove.includes(m.permission_id))
          .map(m => m.bundle_permission_id);
        await Promise.all(removeMappingIds.map(id =>
          routedDelete(session, 'sfk_bundle_permission', 'bundle_permission_id', id)));
      }
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={isCreate ? t('sfk.bundle.new', 'New Duty') : `${t('common.edit', 'Edit')} — ${row?.internal_code ?? ''}`}
      onClose={onClose}
      width="lg"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="internal_code" label={t('common.code', 'Code')}>
        <label className={labelCls}>{t('common.code', 'Code')}</label>
        <input value={code} disabled={!isCreate} onChange={e => setCode(e.target.value.toUpperCase())} className={`${inputCls} font-mono`} placeholder="PURCHASE_ORDER_CREATION" />
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="materiality_amount" label={t('sfk.bundle.materiality', 'Materiality Amount')}>
        <label className={labelCls}>{t('sfk.bundle.materiality', 'Materiality Amount')}</label>
        <input type="number" step="0.01" value={materiality} onChange={e => setMateriality(e.target.value)} className={inputCls} />
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="is_active" label={t('common.boolean.active', 'Active')}>
        <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 accent-gray-700" />
          {t('common.boolean.active', 'Active')}
        </label>
      </PersonalizedField>

      <TranslationsEditor availableLocales={ACTIVE_LOCALES} value={translations} onChange={setTranslations} label={t('common.translations', 'Translations')} />

      {/* Permission binding editor — cross-application picker for this duty. */}
      <div className="mt-4 border-t border-csm-border pt-3">
        <div className="mb-2 flex items-center gap-2">
          <span className="text-csm-sm font-medium text-csm-ink-soft">{t('sfk.bundle.bound_permissions', 'Bound Permissions')}</span>
          <input value={permFilter} onChange={e => setPermFilter(e.target.value)} placeholder={t('sfk.bundle.search_permissions', 'Search permissions across all applications…')}
            className="ml-1 h-7 flex-1 rounded border border-csm-border px-2 text-csm-sm" />
          <ClearFiltersButton active={permFilter.trim() !== ''} onClear={() => setPermFilter('')} />
          <span className="text-csm-xs text-csm-watermark">{selectedPerms.size} {t('sfk.bundle.bound', 'bound')}</span>
        </div>
        <div className="max-h-56 overflow-auto rounded border border-csm-border">
          {filteredPerms.length === 0 && <div className="p-4"><EmptyState title={t('common.no_records', 'No records')} /></div>}
          <div className="flex flex-col gap-0.5 p-1">
            {filteredPerms.map(p => (
              <label key={p.permission_id} className="flex items-center gap-2 rounded px-2 py-1 text-csm-sm hover:bg-csm-surface-alt">
                <input type="checkbox" checked={selectedPerms.has(p.permission_id)} onChange={() => togglePerm(p.permission_id)} className="h-4 w-4 accent-gray-700" />
                <span className="w-40 shrink-0 truncate text-csm-xs text-csm-watermark">{p.application_name}</span>
                <span className="flex-1">{p.display_name}</span>
                <span className="font-mono text-csm-xs text-csm-watermark">{p.platform_signature}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function SfkBundlePage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <BundleInner session={session} />
    </PersonalizationProvider>
  );
}
